import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import IconButtonBox from '../../components/IconButtonBox';
import ButtonBox from '../../components/ButtonBox';
import AddIcon from '@mui/icons-material/Add';
import CachedIcon from '@mui/icons-material/Cached';
import { PROJECT_ID, ROUTE_PROJECT_INFORMATION } from '../../constants';
import DialogBox from '../../components/DialogBox';
import TextInputBox from '../../components/TextInputBox';
import NewTableBox, { RowData } from '../../components/NewTableBox';
import ProjectApi from '../../api/ProjectApi';
import { Project as ProjectType } from "../../types/ProjectType";
import { addLocalStorage, useError } from '../../utils/functions';
import usePaginationStore from '../../stores/PaginationStore';

const Project: React.FC = () => {
    const { setError } = useError();
    const navigate = useNavigate();
    const [data, setData] = useState<ProjectType[]>([]);
    const [open, setOpen] = useState(false);
    const [delOpen, setDelOpen] = useState(false);

    const [totalCount, setTotalCount] = useState(0);
    const { setBeforeCursor, setAfterCursor, setReQuery, pageAction, closeAction, resetStore } = usePaginationStore();

    const [formValues, setFormValues] = useState({
        name: '',
        delId: '',
        delName: '',
    });

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const handleAdd = async () => {
        const res = await ProjectApi.createProject(formValues.name);

        if (res.code) {
            setError(res.message);
        } else {
            handleChange('name', '');
            setReQuery(true);
            queryAllProject();
            handleClose();
        }

    };

    const handleSetEdit = (row: RowData) => {
        addLocalStorage(PROJECT_ID, row['id']);
        navigate(ROUTE_PROJECT_INFORMATION);
    };

    const columns = [
        { field: "name", title: "Name", isLink: true, linkHandler: (val: any, row: RowData) => { handleSetEdit(row) } },
        { field: "updatedAt", title: "Update at" },
        { field: "createdAt", title: "Created at" },
    ];

    const handleChange = (field: string, value: string) => {
        setFormValues((prevValues) => ({ ...prevValues, [field]: value }));
    };

    const queryAllProject = async () => {
        setData([]);
        try {
            const { limit, beforeCursor, afterCursor } = usePaginationStore.getState();
            const paginationParams = {
                limit: limit,
                beforeCursor: beforeCursor,
                afterCursor: afterCursor,
            };

            const projectResponse = await ProjectApi.getProjects(paginationParams);
            if (projectResponse && projectResponse.data.length > 0) {
                setData([...projectResponse.data]);

                if (projectResponse.metadata && projectResponse.metadata.total) {
                    setTotalCount(projectResponse.metadata.total);
                }

                if (projectResponse.paging) {
                    setAfterCursor(projectResponse.paging.afterCursor);
                    setBeforeCursor(projectResponse.paging.beforeCursor);
                }
            }
        } catch (error) {
            console.error("Error fetching projectResponse data:", error);
        } finally {
            closeAction();
        }
    }

    const openDelete = (obj: any) => {
        setDelOpen(true);
        handleChange('delId', obj.id);
        handleChange('delName', obj.name);
    };

    const handleDelete = async () => {
        if (formValues.delId) {
            const res = await ProjectApi.deleteProject(formValues.delId);

            if (res.code) {
                setError(res.message);
            } else {
                setDelOpen(false);
                handleChange('delId', '');
                handleChange('delName', '');
                setReQuery(true);
                queryAllProject();
            }
        }
    };

    useEffect(() => {
        resetStore();
        queryAllProject();
    }, []);

    useEffect(() => {
        if (pageAction) {
            queryAllProject();
        }
    }, [pageAction]);

    return (
        <>
            <Typography className="layout-title">
                Project
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box />
                <Box>
                    <IconButtonBox
                        icon={<CachedIcon />}
                        text='Refresh'
                        onClick={queryAllProject} />
                    <ButtonBox
                        text='Create new'
                        onClick={handleOpen}
                        icon={<AddIcon />} />
                </Box>
            </Box>
            <NewTableBox columns={columns} data={data} showDelete={true} deleteFunction={(rowData) => openDelete(rowData)} totalCount={totalCount} />
            <DialogBox
                open={open}
                title="Create project"
                dialogContent={
                    <>
                        <TextInputBox
                            label="Name"
                            placeholder='Name'
                            required={true}
                            value={formValues.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                        />
                    </>
                }
                onCancel={handleClose}
                onConfirm={handleAdd}
                confirmLabel="Add"
            />
            <DialogBox
                open={delOpen}
                title='Delete'
                dialogContent={
                    <>
                        {formValues.delName} will be permanently deleted.
                    </>
                }
                showCancel={true}
                onCancel={() => setDelOpen(false)}
                onConfirm={handleDelete}
                isError={true}
                confirmLabel='Delete'
            />
        </>
    );
};

export default Project;

